/*!
 * AudoChat - Settings Page
 */

import { useState, useEffect } from "react";

// @mui material components
import { TextField, IconButton, InputAdornment, Card, Grid, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select, Chip } from "@mui/material";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

// Vision UI Dashboard React base styles
import colors from "assets/theme/base/colors";
import linearGradient from "assets/theme/functions/linearGradient";

// React icons
import { IoEye, IoEyeOff, IoCopyOutline } from "react-icons/io5";
import { MdSave, MdAdd, MdEdit, MdDelete } from "react-icons/md";
import { BsShieldCheck, BsPerson } from "react-icons/bs";

// Utils
import { ROLES } from "utils/database";
import auth from "utils/auth";
import api from "utils/api";

const { secondary, info, text, dark, gradients, borderCol, success, error } = colors;
const { card } = gradients;

function Settings() {
  const currentUser = auth.getCurrentUser();
  const isAdmin = currentUser && currentUser.role === ROLES.ADMIN;

  // WhatsApp API Settings
  const [accessToken, setAccessToken] = useState("");
  const [businessAccountId, setBusinessAccountId] = useState("");
  const [showAccessToken, setShowAccessToken] = useState(false);
  const [copied, setCopied] = useState(false);

  // User Management
  const [users, setUsers] = useState([]);
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    password: "",
    role: ROLES.AGENT,
  });

  // Load settings and users on mount
  useEffect(() => {
    loadSettings();
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  const loadSettings = async () => {
    try {
      const settings = await api.getSettings();
      setAccessToken(settings.accessToken || settings.whatsapp_access_token || "");
      setBusinessAccountId(settings.businessAccountId || settings.whatsapp_business_account_id || "");
    } catch (error) {
      console.error("Error loading settings:", error);
    }
  };

  const loadUsers = async () => {
    try {
      const users = await api.getUsers();
      setUsers(users || []);
    } catch (error) {
      console.error("Error loading users:", error);
    }
  };

  const handleCopyToken = () => {
    if (accessToken) {
      navigator.clipboard.writeText(accessToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await api.updateSettings({
        accessToken: accessToken,
        businessAccountId: businessAccountId,
        whatsapp_access_token: accessToken,
        whatsapp_business_account_id: businessAccountId,
      });
      alert("Settings saved successfully!");
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("Failed to save settings: " + error.message);
    }
  };

  // User Management Functions
  const handleOpenUserDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setUserForm({
        name: user.name,
        email: user.email,
        password: "",
        role: user.role,
      });
    } else {
      setEditingUser(null);
      setUserForm({
        name: "",
        email: "",
        password: "",
        role: ROLES.AGENT,
      });
    }
    setOpenUserDialog(true);
  };

  const handleCloseUserDialog = () => {
    setOpenUserDialog(false);
    setEditingUser(null);
    setUserForm({
      name: "",
      email: "",
      password: "",
      role: ROLES.AGENT,
    });
  };

  const handleSaveUser = async () => {
    if (!userForm.name || !userForm.email) {
      alert("Please fill in all required fields");
      return;
    }

    if (!editingUser && !userForm.password) {
      alert("Please enter a password for new user");
      return;
    }

    try {
      if (editingUser) {
        const updates = {
          name: userForm.name,
          email: userForm.email,
          role: userForm.role,
        };
        if (userForm.password) {
          updates.password = userForm.password;
        }
        await api.updateUser(editingUser.id, updates);
        await loadUsers();
        handleCloseUserDialog();
        alert("User updated successfully!");
      } else {
        await api.createUser({
          name: userForm.name,
          email: userForm.email,
          password: userForm.password,
          role: userForm.role,
        });
        await loadUsers();
        handleCloseUserDialog();
        alert("User created successfully!");
      }
    } catch (error) {
      console.error("Error saving user:", error);
      alert("Failed to save user: " + error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (userId === currentUser.id) {
      alert("You cannot delete your own account!");
      return;
    }
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await api.deleteUser(userId);
        await loadUsers();
        alert("User deleted successfully!");
      } catch (error) {
        console.error("Error deleting user:", error);
        alert("Failed to delete user: " + error.message);
      }
    }
  };

  // Table columns for users
  const userTableColumns = [
    { name: "name", align: "left" },
    { name: "email", align: "left" },
    { name: "role", align: "center" },
    { name: "created", align: "left" },
    { name: "action", align: "center" },
  ];

  const userTableRows = users.map((user) => ({
    name: (
      <VuiBox display="flex" alignItems="center">
        <VuiTypography variant="button" color="white" fontWeight="medium">
          {user.name}
        </VuiTypography>
      </VuiBox>
    ),
    email: (
      <VuiTypography variant="button" color="text" fontWeight="regular">
        {user.email}
      </VuiTypography>
    ),
    role: (
      <Chip
        icon={user.role === ROLES.ADMIN ? <BsShieldCheck size={14} /> : <BsPerson size={14} />}
        label={user.role === ROLES.ADMIN ? "Admin" : "Agent"}
        size="small"
        sx={{
          backgroundColor: user.role === ROLES.ADMIN ? success.main : info.main,
          color: "white",
          fontWeight: "bold",
          "& .MuiChip-icon": {
            color: "white",
          },
        }}
      />
    ),
    created: (
      <VuiTypography variant="caption" color="text" fontWeight="regular">
        {user.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}
      </VuiTypography>
    ),
    action: (
      <VuiBox display="flex" gap={1}>
        <IconButton
          onClick={() => handleOpenUserDialog(user)}
          sx={{
            color: info.main,
            "&:hover": {
              backgroundColor: "rgba(0, 117, 255, 0.1)",
            },
          }}
        >
          <MdEdit size={18} />
        </IconButton>
        <IconButton
          onClick={() => handleDeleteUser(user.id)}
          disabled={user.id === currentUser.id}
          sx={{
            color: error.main,
            "&:hover": {
              backgroundColor: "rgba(227, 26, 26, 0.1)",
            },
            "&:disabled": {
              opacity: 0.3,
            },
          }}
        >
          <MdDelete size={18} />
        </IconButton>
      </VuiBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox mb={3}>
          <VuiTypography variant="lg" color="white" fontWeight="bold">
            Settings
          </VuiTypography>
          <VuiTypography variant="button" color={text.main} sx={{ mt: 0.5 }}>
            Manage your application settings and users
          </VuiTypography>
        </VuiBox>

        <Grid container spacing={3}>
          {/* WhatsApp API Settings */}
          <Grid item xs={12}>
            <Card>
              <VuiBox
                p={3}
                sx={{
                  background: linearGradient(card.main, card.state, card.deg),
                  borderBottom: `1px solid ${borderCol.navbar}`,
                }}
              >
                <VuiTypography variant="lg" color="white" fontWeight="bold">
                  WhatsApp API Configuration
                </VuiTypography>
                <VuiTypography variant="caption" color={text.main} sx={{ mt: 0.5 }}>
                  Enter your WhatsApp Business API credentials to enable messaging features
                </VuiTypography>
              </VuiBox>

              <VuiBox p={3}>
                <VuiBox mb={3}>
                  <VuiTypography variant="button" color="white" fontWeight="medium" mb={1}>
                    Access Token *
                  </VuiTypography>
                  <TextField
                    fullWidth
                    type={showAccessToken ? "text" : "password"}
                    placeholder="Enter your WhatsApp API Access Token"
                    value={accessToken}
                    onChange={(e) => setAccessToken(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <VuiBox display="flex" alignItems="center" gap={0.5}>
                            <IconButton
                              onClick={() => setShowAccessToken(!showAccessToken)}
                              sx={{
                                color: text.main,
                                "&:hover": {
                                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                                },
                              }}
                            >
                              {showAccessToken ? <IoEyeOff size={20} /> : <IoEye size={20} />}
                            </IconButton>
                            <IconButton
                              onClick={handleCopyToken}
                              disabled={!accessToken}
                              sx={{
                                color: text.main,
                                "&:hover": {
                                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                                },
                                "&:disabled": {
                                  opacity: 0.5,
                                },
                              }}
                            >
                              <IoCopyOutline size={20} />
                            </IconButton>
                          </VuiBox>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: dark.body,
                        color: "white",
                        borderRadius: "8px",
                        border: `1px solid ${borderCol.navbar}`,
                        "& fieldset": {
                          borderColor: borderCol.navbar,
                        },
                        "&:hover": {
                          backgroundColor: secondary.focus,
                          "& fieldset": {
                            borderColor: info.main,
                          },
                        },
                        "&.Mui-focused": {
                          backgroundColor: secondary.focus,
                          "& fieldset": {
                            borderColor: info.main,
                            borderWidth: "1px",
                          },
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: "white",
                        fontSize: "14px",
                        fontFamily: showAccessToken ? "monospace" : "inherit",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: text.main,
                        opacity: 0.6,
                      },
                    }}
                  />
                  {copied && (
                    <VuiTypography variant="caption" color={info.main} sx={{ mt: 0.5, display: "block" }}>
                      Token copied to clipboard!
                    </VuiTypography>
                  )}
                  <VuiTypography variant="caption" color={text.main} sx={{ mt: 0.5, display: "block" }}>
                    Your WhatsApp API access token. Keep it secure and never share it publicly.
                  </VuiTypography>
                </VuiBox>

                <VuiBox mb={3}>
                  <VuiTypography variant="button" color="white" fontWeight="medium" mb={1}>
                    Business Account ID *
                  </VuiTypography>
                  <TextField
                    fullWidth
                    type="text"
                    placeholder="Enter your WhatsApp Business Account ID"
                    value={businessAccountId}
                    onChange={(e) => setBusinessAccountId(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: dark.body,
                        color: "white",
                        borderRadius: "8px",
                        border: `1px solid ${borderCol.navbar}`,
                        "& fieldset": {
                          borderColor: borderCol.navbar,
                        },
                        "&:hover": {
                          backgroundColor: secondary.focus,
                          "& fieldset": {
                            borderColor: info.main,
                          },
                        },
                        "&.Mui-focused": {
                          backgroundColor: secondary.focus,
                          "& fieldset": {
                            borderColor: info.main,
                            borderWidth: "1px",
                          },
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: "white",
                        fontSize: "14px",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: text.main,
                        opacity: 0.6,
                      },
                    }}
                  />
                  <VuiTypography variant="caption" color={text.main} sx={{ mt: 0.5, display: "block" }}>
                    Your WhatsApp Business Account ID from Meta Business Manager.
                  </VuiTypography>
                </VuiBox>

                <VuiBox display="flex" justifyContent="flex-end" gap={2}>
                  <VuiButton
                    variant="outlined"
                    color="text"
                    onClick={() => {
                      setAccessToken("");
                      setBusinessAccountId("");
                    }}
                  >
                    Reset
                  </VuiButton>
                  <VuiButton
                    variant="gradient"
                    color="info"
                    startIcon={<MdSave size={18} />}
                    onClick={handleSaveSettings}
                    disabled={!accessToken || !businessAccountId}
                  >
                    Save Settings
                  </VuiButton>
                </VuiBox>
              </VuiBox>
            </Card>
          </Grid>

          {/* User Management - Only for Admins */}
          {isAdmin && (
            <Grid item xs={12}>
              <Card>
                <VuiBox
                  p={3}
                  sx={{
                    background: linearGradient(card.main, card.state, card.deg),
                    borderBottom: `1px solid ${borderCol.navbar}`,
                  }}
                >
                  <VuiBox display="flex" justifyContent="space-between" alignItems="center">
                    <VuiBox>
                      <VuiTypography variant="lg" color="white" fontWeight="bold">
                        User Management
                      </VuiTypography>
                      <VuiTypography variant="caption" color={text.main} sx={{ mt: 0.5 }}>
                        Create and manage user accounts and roles
                      </VuiTypography>
                    </VuiBox>
                    <VuiButton
                      variant="gradient"
                      color="info"
                      startIcon={<MdAdd size={18} />}
                      onClick={() => handleOpenUserDialog()}
                    >
                      Add User
                    </VuiButton>
                  </VuiBox>
                </VuiBox>

                <VuiBox p={3}>
                  {users.length > 0 ? (
                    <Table columns={userTableColumns} rows={userTableRows} />
                  ) : (
                    <VuiBox p={4} textAlign="center">
                      <VuiTypography variant="h6" color={text.main}>
                        No users found
                      </VuiTypography>
                    </VuiBox>
                  )}
                </VuiBox>
              </Card>
            </Grid>
          )}
        </Grid>
      </VuiBox>
      <Footer />

      {/* Add/Edit User Dialog */}
      <Dialog
        open={openUserDialog}
        onClose={handleCloseUserDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: dark.body,
            backgroundImage: "none",
            borderRadius: "16px",
            border: `1px solid ${borderCol.navbar}`,
          },
        }}
      >
        <DialogTitle sx={{ color: "white", borderBottom: `1px solid ${borderCol.navbar}` }}>
          <VuiTypography variant="lg" color="white" fontWeight="bold">
            {editingUser ? "Edit User" : "Create New User"}
          </VuiTypography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <VuiBox display="flex" flexDirection="column" gap={2}>
            <VuiBox>
              <VuiTypography variant="button" color="white" fontWeight="medium" mb={1}>
                Name *
              </VuiTypography>
              <TextField
                fullWidth
                placeholder="Enter user name"
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: dark.body,
                    color: "white",
                    "& fieldset": {
                      borderColor: borderCol.navbar,
                    },
                    "&:hover fieldset": {
                      borderColor: info.main,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: info.main,
                    },
                  },
                  "& .MuiInputBase-input": {
                    color: "white",
                  },
                }}
              />
            </VuiBox>

            <VuiBox>
              <VuiTypography variant="button" color="white" fontWeight="medium" mb={1}>
                Email *
              </VuiTypography>
              <TextField
                fullWidth
                type="email"
                placeholder="Enter user email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                disabled={!!editingUser}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: dark.body,
                    color: "white",
                    "& fieldset": {
                      borderColor: borderCol.navbar,
                    },
                    "&:hover fieldset": {
                      borderColor: info.main,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: info.main,
                    },
                  },
                  "& .MuiInputBase-input": {
                    color: "white",
                  },
                }}
              />
            </VuiBox>

            <VuiBox>
              <VuiTypography variant="button" color="white" fontWeight="medium" mb={1}>
                Password {editingUser ? "(leave empty to keep current)" : "*"}
              </VuiTypography>
              <TextField
                fullWidth
                type="password"
                placeholder={editingUser ? "Enter new password (optional)" : "Enter password"}
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: dark.body,
                    color: "white",
                    "& fieldset": {
                      borderColor: borderCol.navbar,
                    },
                    "&:hover fieldset": {
                      borderColor: info.main,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: info.main,
                    },
                  },
                  "& .MuiInputBase-input": {
                    color: "white",
                  },
                }}
              />
            </VuiBox>

            <VuiBox>
              <VuiTypography variant="button" color="white" fontWeight="medium" mb={1}>
                Role *
              </VuiTypography>
              <Select
                fullWidth
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                sx={{
                  backgroundColor: dark.body,
                  color: "white",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: borderCol.navbar,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: info.main,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: info.main,
                  },
                  "& .MuiSvgIcon-root": {
                    color: "white",
                  },
                }}
              >
                <MenuItem value={ROLES.AGENT}>Agent (Chat, Dashboard, CRM)</MenuItem>
                <MenuItem value={ROLES.ADMIN}>Admin (Full Access)</MenuItem>
              </Select>
              <VuiTypography variant="caption" color={text.main} sx={{ mt: 0.5, display: "block" }}>
                {userForm.role === ROLES.ADMIN
                  ? "Admin can access all features including user management"
                  : "Agent can access Chat, Dashboard, CRM, Items, and Billing"}
              </VuiTypography>
            </VuiBox>
          </VuiBox>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${borderCol.navbar}` }}>
          <VuiButton variant="outlined" color="text" onClick={handleCloseUserDialog}>
            Cancel
          </VuiButton>
          <VuiButton variant="gradient" color="info" onClick={handleSaveUser}>
            {editingUser ? "Update User" : "Create User"}
          </VuiButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Settings;
